import { FormSelect, Modal, ModalSizeEnum, PrimaryButton } from '@systemeio/ui'
import { CampaignId } from 'modules/campaigns/types/campaign-interface'
import { useContactsImportColumnsSelectOptions } from 'modules/contacts/contacts-import/hook/use-contacts-import-columns-select-options'
import { useGetPreviewOfImportContactsTable } from 'modules/contacts/contacts-import/hook/use-get-preview-of-import-contacts-table'
import { useSendImportContacts } from 'modules/contacts/contacts-import/hook/use-send-import-contacts'
import { ContactsImportColumnsSelectedColumnsFrontend } from 'modules/contacts/contacts-import/types/contacts-import-column-select'
import { ContactsImportDelimiter } from 'modules/contacts/contacts-import/types/contacts-import-delimeter-type'
import {
  ContactsImportFileColumnName,
  ContactsImportFileDataFrontendType,
} from 'modules/contacts/contacts-import/types/contacts-import-file-interface'
import { ContactBulkApiInterface } from 'modules/contacts/types/contact-menu-interface'
import React, { useEffect, useMemo, useState } from 'react'
import {
  ColumnWithExtraHeaderInterface,
  TableWithExtraHeaders,
} from 'shared/components/table/table-with-extra-headers'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { DataWithPaginationInterface } from 'shared/types/pagination-interface'
import { ArrowRightIcon } from '@systemeio/ui/icons'

export interface SelectColumnsForContactsImportModalProps {
  file: File | null
  delimiter: ContactsImportDelimiter
  campaign: CampaignId | undefined
  tags: Array<ContactBulkApiInterface> | undefined
  onConfirm: () => Promise<void>
  clearStates: () => void
}

export const SelectColumnsForContactsImportModal = ({
  file,
  delimiter,
  campaign,
  tags,
  onConfirm,
  clearStates,
}: SelectColumnsForContactsImportModalProps) => {
  const { t } = useLocoTranslation()

  const [isOpened, setIsOpened] = useState<boolean>(false)
  const [selectedColumns, setSelectedColumns] =
    useState<ContactsImportColumnsSelectedColumnsFrontend>({})
  const [fileData, setFileData] = useState<DataWithPaginationInterface<
    ContactsImportFileDataFrontendType[0]
  > | null>(null)
  const [fileHeaders, setFileHeaders] = useState<ContactsImportFileColumnName[]>([])
  const [fileHasHeaders, setFileHasHeaders] = useState<boolean>()
  const { contactsImportColumnsSelectOptions } = useContactsImportColumnsSelectOptions()
  const { fetcherGetPreviewOfImportContactsTable, isGetPreviewOfImportContactsTableFetching } =
    useGetPreviewOfImportContactsTable()

  const {
    fetcherSendImportContacts,
    isSendImportContactsFetching,
    sendImportContactsError,
    resetSendImportContactsError,
  } = useSendImportContacts()

  const onClose = () => {
    setIsOpened(false)
    resetSendImportContactsError()
  }

  const columns: ColumnWithExtraHeaderInterface<ContactsImportFileDataFrontendType[0]>[] = useMemo(
    () =>
      fileHeaders.map((columnName, index) => ({
        key: columnName,
        header: columnName,
        widthPercent: 25,
        extraHeader: (
          <FormSelect
            data={contactsImportColumnsSelectOptions}
            onChange={value => {
              setSelectedColumns(prev => ({ ...prev, [index]: value ?? null }))
            }}
            value={selectedColumns[index]}
            error={sendImportContactsError?.columns[index]}
            isPreFetching={!contactsImportColumnsSelectOptions}
          />
        ),
      })),
    [contactsImportColumnsSelectOptions, fileHeaders, selectedColumns, sendImportContactsError],
  )

  const onChooseData = async () => {
    try {
      const { headers, dataForFrontend, fileHasHeaders } =
        await fetcherGetPreviewOfImportContactsTable({
          file: file as File,
          delimiter,
        })
      setFileHeaders(headers)
      setFileData(dataForFrontend)
      setFileHasHeaders(fileHasHeaders)
      setIsOpened(true)
    } catch (e) {}
  }

  const handleConfirm = async () => {
    try {
      await fetcherSendImportContacts({
        file,
        delimiter,
        campaign,
        tags,
        selectedColumnsFrontend: selectedColumns,
      })
      setIsOpened(false)
      await onConfirm()
      clearStates()
    } catch (e) {}
  }

  useEffect(() => {
    if (fileData && contactsImportColumnsSelectOptions) {
      contactsImportColumnsSelectOptions?.forEach(option =>
        fileHeaders.some((headerValue, headerIndex) => {
          if (option.caption.toLowerCase() === headerValue.toLocaleLowerCase()) {
            setSelectedColumns(prev => ({
              ...prev,
              [headerIndex]: option.id,
            }))
            return true
          } else {
            return false
          }
        }),
      )
    }
  }, [contactsImportColumnsSelectOptions, fileData, fileHeaders])

  return (
    <>
      <PrimaryButton
        disabled={!file}
        isFetching={isGetPreviewOfImportContactsTableFetching}
        onClick={onChooseData}
      >
        {t('global.next')}
        <ArrowRightIcon />
      </PrimaryButton>
      <Modal
        className="max-w-[100vw]"
        size={ModalSizeEnum.medium}
        isCentered
        opened={isOpened}
        onClose={onClose}
        title={t('dashboard.contact_import.select_columns_table.title')}
      >
        <form className="m-auto flex max-w-fit flex-col gap-6 overflow-hidden lg:gap-10">
          {sendImportContactsError?.common && (
            <p
              className="text-danger text-center text-sm"
              dangerouslySetInnerHTML={{ __html: sendImportContactsError.common }}
            ></p>
          )}
          <TableWithExtraHeaders
            data={fileData || undefined}
            columns={columns}
            noHeader={!fileHasHeaders}
          />
          <div className="flex justify-center">
            <PrimaryButton
              isFetching={isSendImportContactsFetching}
              disabled={!contactsImportColumnsSelectOptions}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                handleConfirm()
              }}
            >
              {t('dashboard.contact_import.import_table.import_button.label')}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </>
  )
}
